import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiList, apiPost, tokenStore } from '@/lib/api';
import type {
  Campaign,
  CampaignRecipient,
  DocumentTemplate,
  Folder,
  Journey,
  JourneyCatalogue,
  MarketingSettings,
  PreSendCheck,
  Segment,
  SegmentField,
  SegmentPreview,
  SignatureRequest,
  StorageReport,
  Suppression,
  VaultDocument,
  WhatsappAccount,
  WhatsappConversation,
  WhatsappTemplate,
} from './types';

function clean<T extends Record<string, unknown>>(params: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null),
  ) as Partial<T>;
}

function invalidateVault(queryClient: ReturnType<typeof useQueryClient>) {
  for (const key of ['documents', 'folders', 'signatures', 'document-templates']) {
    void queryClient.invalidateQueries({ queryKey: [key] });
  }
}

function invalidateMarketing(queryClient: ReturnType<typeof useQueryClient>) {
  for (const key of ['segments', 'campaigns', 'marketing', 'journeys', 'whatsapp']) {
    void queryClient.invalidateQueries({ queryKey: [key] });
  }
}

// ── Documents ────────────────────────────────────────────────────────────────

export function useDocuments(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: ['documents', params],
    queryFn: () => apiList<VaultDocument[]>('/documents', { params: clean(params) }),
  });
}

export function useDocument(id: string | undefined) {
  return useQuery({
    queryKey: ['documents', id],
    queryFn: () => apiGet<VaultDocument>(`/documents/${id}`),
    enabled: Boolean(id),
  });
}

export function useFolders() {
  return useQuery({
    queryKey: ['folders'],
    queryFn: () => apiGet<Folder[]>('/documents/folders'),
  });
}

export function useStorageReport() {
  return useQuery({
    queryKey: ['documents', 'storage'],
    queryFn: () => apiGet<StorageReport>('/documents/storage'),
  });
}

/**
 * Uploads a file.
 *
 * Uses fetch rather than the shared client because that one serialises JSON,
 * and a multipart body must set its own boundary.
 */
export function useUploadDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { file: File; meta: Record<string, string | undefined> }) => {
      const body = new FormData();
      body.append('file', input.file);
      for (const [key, value] of Object.entries(input.meta)) {
        if (value !== undefined && value !== '') body.append(key, value);
      }

      const base = import.meta.env.VITE_API_URL ?? '/api/v1';
      const response = await fetch(`${base}/documents/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tokenStore.access ?? ''}` },
        body,
      });

      const json = (await response.json()) as { success: boolean; data?: VaultDocument; error?: { message: string } };
      if (!response.ok || !json.success) {
        throw new Error(json.error?.message ?? 'Could not upload that file');
      }
      return json.data as VaultDocument;
    },
    onSuccess: () => invalidateVault(queryClient),
  });
}

export function useSaveDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id: string }) =>
      apiPost<VaultDocument>(`/documents/${id}`, { ...body, id }),
    onSuccess: () => invalidateVault(queryClient),
  });
}

export function useDeleteDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/documents/${id}`),
    onSuccess: () => invalidateVault(queryClient),
  });
}

export function useDownloadUrl() {
  return useMutation({
    mutationFn: ({ id, versionId }: { id: string; versionId?: string }) =>
      apiGet<{ url: string; filename: string; mimeType: string; previewable: boolean }>(
        `/documents/${id}/download-url`,
        { params: clean({ versionId }) },
      ),
  });
}

export function useRestoreVersion(documentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (versionId: string) =>
      apiPost(`/documents/${documentId}/versions/${versionId}/restore`),
    onSuccess: () => invalidateVault(queryClient),
  });
}

export function useSetDocumentPermissions(documentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (grants: unknown[]) => apiPost(`/documents/${documentId}/permissions`, { grants }),
    onSuccess: () => invalidateVault(queryClient),
  });
}

export function useSaveFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost<Folder>('/document-folders', body),
    onSuccess: () => invalidateVault(queryClient),
  });
}

export function useDeleteFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/document-folders/${id}`),
    onSuccess: () => invalidateVault(queryClient),
  });
}

// ── Sharing ──────────────────────────────────────────────────────────────────

export function useCreateShare(documentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiPost<{ id: string; token: string; url: string }>(`/documents/${documentId}/shares`, body),
    onSuccess: () => invalidateVault(queryClient),
  });
}

export function useRevokeShare(documentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (shareId: string) => apiDelete(`/documents/${documentId}/shares/${shareId}`),
    onSuccess: () => invalidateVault(queryClient),
  });
}

// ── Signatures ───────────────────────────────────────────────────────────────

export function useSignatureRequests(status?: string) {
  return useQuery({
    queryKey: ['signatures', status],
    queryFn: () => apiGet<SignatureRequest[]>('/signatures', { params: clean({ status }) }),
  });
}

export function useSignatureRequest(id: string | undefined) {
  return useQuery({
    queryKey: ['signatures', id],
    queryFn: () => apiGet<SignatureRequest>(`/signatures/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateSignatureRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost<SignatureRequest>('/signatures', body),
    onSuccess: () => invalidateVault(queryClient),
  });
}

export function useSendSignatureRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<SignatureRequest>(`/signatures/${id}/send`),
    onSuccess: () => invalidateVault(queryClient),
  });
}

export function useCancelSignatureRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost(`/signatures/${id}/cancel`),
    onSuccess: () => invalidateVault(queryClient),
  });
}

// ── Templates ────────────────────────────────────────────────────────────────

export function useDocumentTemplates() {
  return useQuery({
    queryKey: ['document-templates'],
    queryFn: () => apiGet<DocumentTemplate[]>('/document-templates'),
  });
}

export function useSaveDocumentTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost('/document-templates', body),
    onSuccess: () => invalidateVault(queryClient),
  });
}

export function useDeleteDocumentTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/document-templates/${id}`),
    onSuccess: () => invalidateVault(queryClient),
  });
}

export function useTemplatePreview(templateId?: string, entityId?: string) {
  return useQuery({
    queryKey: ['document-templates', templateId, 'preview', entityId],
    queryFn: () =>
      apiGet<{ html: string; values: Record<string, string>; unresolved: string[] }>(
        `/document-templates/${templateId}/preview`,
        { params: { entityId: entityId as string } },
      ),
    enabled: Boolean(templateId && entityId),
    retry: false,
  });
}

export function useGenerateDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiPost<{ id: string; name: string; unresolved: string[] }>('/document-templates/generate', body),
    onSuccess: () => invalidateVault(queryClient),
  });
}

// ── Segments ─────────────────────────────────────────────────────────────────

export function useSegments() {
  return useQuery({ queryKey: ['segments'], queryFn: () => apiGet<Segment[]>('/segments') });
}

export function useSegmentFields() {
  return useQuery({
    queryKey: ['segments', 'fields'],
    queryFn: () => apiGet<SegmentField[]>('/segments/fields'),
    staleTime: 10 * 60 * 1000,
  });
}

export function usePreviewSegment() {
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost<SegmentPreview>('/segments/preview', body),
  });
}

export function useSaveSegment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost<Segment>('/segments', body),
    onSuccess: () => invalidateMarketing(queryClient),
  });
}

export function useDeleteSegment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/segments/${id}`),
    onSuccess: () => invalidateMarketing(queryClient),
  });
}

// ── Campaigns ────────────────────────────────────────────────────────────────

export function useCampaigns(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: ['campaigns', params],
    queryFn: () => apiGet<Campaign[]>('/campaigns', { params: clean(params) }),
  });
}

export function useCampaign(id: string | undefined) {
  return useQuery({
    queryKey: ['campaigns', id],
    queryFn: () => apiGet<Campaign>(`/campaigns/${id}`),
    enabled: Boolean(id),
  });
}

export function useCampaignCheck(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['campaigns', id, 'check'],
    queryFn: () => apiGet<PreSendCheck>(`/campaigns/${id}/check`),
    enabled: Boolean(id) && enabled,
    retry: false,
  });
}

export function useCampaignRecipients(id: string | undefined, status?: string) {
  return useQuery({
    queryKey: ['campaigns', id, 'recipients', status],
    queryFn: () =>
      apiGet<CampaignRecipient[]>(`/campaigns/${id}/recipients`, { params: clean({ status }) }),
    enabled: Boolean(id),
  });
}

export function useSaveCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id?: string }) =>
      apiPost<Campaign>(id ? `/campaigns/${id}` : '/campaigns', body),
    onSuccess: () => invalidateMarketing(queryClient),
  });
}

export function useTestSend(id: string) {
  return useMutation({
    mutationFn: (to: string) => apiPost(`/campaigns/${id}/test-send`, { to }),
  });
}

export function useScheduleCampaign(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost(`/campaigns/${id}/schedule`, body),
    onSuccess: () => invalidateMarketing(queryClient),
  });
}

export function useCampaignAction(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (action: 'pause' | 'resume' | 'cancel') => apiPost(`/campaigns/${id}/${action}`),
    onSuccess: () => invalidateMarketing(queryClient),
  });
}

// ── Marketing settings and suppression ───────────────────────────────────────

export function useMarketingSettings() {
  return useQuery({
    queryKey: ['marketing', 'settings'],
    queryFn: () => apiGet<MarketingSettings>('/marketing/settings'),
  });
}

export function useSuppressions(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: ['marketing', 'suppressions', params],
    queryFn: () => apiList<Suppression[]>('/marketing/suppressions', { params: clean(params) }),
  });
}

export function useSuppress() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost('/marketing/suppressions', body),
    onSuccess: () => invalidateMarketing(queryClient),
  });
}

export function useUnsuppress() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/marketing/suppressions/${id}`),
    onSuccess: () => invalidateMarketing(queryClient),
  });
}

export function useSetConsent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost('/marketing/consent', body),
    onSuccess: () => invalidateMarketing(queryClient),
  });
}

// ── Journeys ─────────────────────────────────────────────────────────────────

export function useJourneys() {
  return useQuery({ queryKey: ['journeys'], queryFn: () => apiGet<Journey[]>('/journeys') });
}

export function useJourneyCatalogue() {
  return useQuery({
    queryKey: ['journeys', 'catalogue'],
    queryFn: () => apiGet<JourneyCatalogue>('/journeys/catalogue'),
    staleTime: 10 * 60 * 1000,
  });
}

export function useSaveJourney() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost<Journey>('/journeys', body),
    onSuccess: () => invalidateMarketing(queryClient),
  });
}

export function useSetJourneyStatus(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (status: string) => apiPost(`/journeys/${id}/status`, { status }),
    onSuccess: () => invalidateMarketing(queryClient),
  });
}

export function useJourneyEnrolments(id: string | undefined) {
  return useQuery({
    queryKey: ['journeys', id, 'enrolments'],
    queryFn: () => apiGet<unknown[]>(`/journeys/${id}/enrolments`),
    enabled: Boolean(id),
  });
}

// ── WhatsApp ─────────────────────────────────────────────────────────────────

export function useWhatsappAccounts() {
  return useQuery({
    queryKey: ['whatsapp', 'accounts'],
    queryFn: () => apiGet<WhatsappAccount[]>('/whatsapp/accounts'),
  });
}

export function useSaveWhatsappAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost('/whatsapp/accounts', body),
    onSuccess: () => invalidateMarketing(queryClient),
  });
}

export function useWhatsappTemplates(accountId?: string) {
  return useQuery({
    queryKey: ['whatsapp', 'templates', accountId],
    queryFn: () =>
      apiGet<WhatsappTemplate[]>('/whatsapp/templates', { params: clean({ accountId }) }),
  });
}

export function useSaveWhatsappTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost('/whatsapp/templates', body),
    onSuccess: () => invalidateMarketing(queryClient),
  });
}

export function useSyncWhatsappTemplates() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accountId: string) => apiPost(`/whatsapp/accounts/${accountId}/sync-templates`),
    onSuccess: () => invalidateMarketing(queryClient),
  });
}

export function useConversations(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: ['whatsapp', 'conversations', params],
    queryFn: () =>
      apiGet<WhatsappConversation[]>('/whatsapp/conversations', { params: clean(params) }),
    refetchInterval: 60_000,
  });
}

export function useConversation(id: string | undefined) {
  return useQuery({
    queryKey: ['whatsapp', 'conversations', id],
    queryFn: () => apiGet<WhatsappConversation>(`/whatsapp/conversations/${id}`),
    enabled: Boolean(id),
    refetchInterval: 30_000,
  });
}

export function useSendWhatsapp(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiPost(`/whatsapp/conversations/${conversationId}/send`, body),
    onSuccess: () => invalidateMarketing(queryClient),
  });
}

export function useAssignConversation(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (assignedToId: string | null) =>
      apiPost(`/whatsapp/conversations/${conversationId}/assign`, { assignedToId }),
    onSuccess: () => invalidateMarketing(queryClient),
  });
}
